// Portuguese (pt-AO) UI strings — single source of truth for all visible text.
// At the bottom of this file the canonical `pt` dict is wrapped in a reactive
// Proxy (default export) so `import pt from "@/lib/i18n"` automatically picks
// English (with PT fallback) when the user toggles language in their profile.
import en from "./i18n_en";

const pt = {
    auth: {
        loginTitle: "NexusTel",
        loginTagline: "Cada ligação, gerida com inteligência.",
        emailLabel: "Endereço de email",
        emailPlaceholder: "nome@angolateleccom.ao",
        passwordLabel: "Palavra-passe",
        passwordPlaceholder: "Introduza a sua palavra-passe",
        submit: "Entrar na Plataforma",
        submitting: "A autenticar…",
        invalidCredentials: "Credenciais inválidas. Verifique e tente novamente.",
        showPassword: "Mostrar palavra-passe",
        hidePassword: "Ocultar palavra-passe",
        footer: "© 2026 NexusTel — Angola Telecom",
        forgotPassword: "Esqueceu a palavra-passe?",
        sessionExpired: "Sessão expirada. Por favor inicie sessão novamente.",
    },
    nav: {
        dashboard: "Painel Principal",
        services: "Serviços",
        customers: "Clientes",
        fieldOps: "Operações de Campo",
        cx: "Serviço ao Cliente",
        sales: "Vendas B2B",
        revenue: "Receita",
        analytics: "Analítica",
        integrations: "Integrações",
        admin: "Administração",
        users: "Utilizadores",
        profile: "Perfil",
        logout: "Terminar Sessão",
        section_operations: "Operações",
        section_intelligence: "Inteligência",
        section_platform: "Plataforma",
    },
    dashboard: {
        greetingMorning: "Bom dia",
        greetingAfternoon: "Boa tarde",
        greetingEvening: "Boa noite",
        kpi_activations_today: "Activações Hoje",
        kpi_open_tickets: "Tickets Abertos",
        kpi_csat: "CSAT Médio",
        kpi_pipeline: "Pipeline B2B",
        quickActions: "Acções Rápidas",
        recentActivity: "Actividade Recente",
        aiSummary: "Resumo Executivo IA",
        aiSummaryPlaceholder:
            "Disponível após configuração do módulo Analítica. O assistente irá gerar um briefing diário do estado da operação.",
        comingSoon: "Em breve",
        viewAll: "Ver tudo",
        operationalStatus: "Sistemas operacionais",
        quick: {
            services: "Provisionar Serviço",
            servicesDesc: "Criar nova activação ou alteração",
            field: "Despachar Técnico",
            fieldDesc: "Atribuir ordem de serviço",
            tickets: "Abrir Ticket",
            ticketsDesc: "Registar incidente ou pedido",
            sales: "Nova Oportunidade",
            salesDesc: "Iniciar qualificação B2B",
        },
    },
    users: {
        title: "Gestão de Utilizadores",
        subtitle: "Crie e gira as contas da sua organização.",
        add: "Adicionar Utilizador",
        searchPlaceholder: "Pesquisar por nome ou email…",
        filterRole: "Todos os perfis",
        filterStatus: "Todos os estados",
        active: "Activo",
        inactive: "Inactivo",
        col_user: "Utilizador",
        col_email: "Email",
        col_role: "Perfil",
        col_lastLogin: "Último acesso",
        col_status: "Estado",
        col_actions: "Acções",
        empty: "Nenhum utilizador encontrado.",
        neverLogged: "Nunca acedeu",
        confirmDeactivate: "Tem a certeza que deseja desactivar este utilizador?",
        roleLabels: {
            super_admin: "Super Administrador",
            operations_manager: "Gestor de Operações",
            field_technician: "Técnico de Campo",
            customer_service_agent: "Agente de Apoio ao Cliente",
            sales_manager: "Gestor de Vendas",
            sales_rep: "Comercial B2B",
            it_admin: "Administrador TI",
            read_only_viewer: "Observador",
        },
        form: {
            fullName: "Nome completo",
            email: "Email corporativo",
            password: "Palavra-passe inicial",
            role: "Perfil de acesso",
            phone: "Telemóvel (opcional)",
            submit: "Criar Utilizador",
            cancel: "Cancelar",
        },
    },
    profile: {
        title: "O Meu Perfil",
        subtitle: "Gira a sua informação pessoal e preferências.",
        personalInfo: "Informação Pessoal",
        changePassword: "Alterar Palavra-passe",
        currentPassword: "Palavra-passe actual",
        newPassword: "Nova palavra-passe",
        confirmNew: "Confirmar nova palavra-passe",
        savePassword: "Actualizar palavra-passe",
        saveProfile: "Guardar alterações",
        savedOk: "Alterações guardadas com sucesso.",
        passwordOk: "Palavra-passe actualizada com sucesso.",
        passwordMismatch: "As palavras-passe não coincidem.",
    },
    notifications: {
        title: "Notificações",
        markAllRead: "Marcar todas como lidas",
        empty: "Está tudo em dia.",
        today: "Hoje",
        yesterday: "Ontem",
        thisWeek: "Esta semana",
        older: "Anterior",
    },
    common: {
        save: "Guardar",
        cancel: "Cancelar",
        confirm: "Confirmar",
        delete: "Eliminar",
        edit: "Editar",
        close: "Fechar",
        loading: "A carregar…",
        searching: "A pesquisar…",
        of: "de",
        page: "Página",
        none: "—",
        actions: "Acções",
        description: "Descrição",
        assignedTo: "Atribuído a",
        assigned: "Atribuído",
        service: "Serviço",
        session: "Sessão",
        sessionActive: "Sessão activa",
        waiting: "A aguardar",
        info: "Informações",
        noComments: "Nenhum comentário ainda.",
        noServices: "Nenhum serviço encontrado.",
        noDocuments: "Sem documentos.",
        noTickets: "Sem tickets.",
        noLinkedTickets: "Sem tickets ligados.",
        noJobs: "Sem trabalhos",
        noDeals: "Sem negócios.",
        noNotes: "Sem notas. Clique no botão acima para gerar com IA.",
        serviceOrder: "Ordem de serviço",
        lastSync: "Última Sync",
        rejectionReason: "Motivo da rejeição",
        rejectOrder: "Rejeitar ordem",
        chartActivations: "Activações",
        chartCsat: "CSAT médio",
        completed: "Concluído",
        switchLangToEn: "Mudar para Inglês",
        switchLangToPt: "Switch to Portuguese",
        pwaInstallHint: "Acesso rápido e modo offline para o terreno.",
        csatChurnHint: "Clientes com avaliação 1-2 ★ recentes — risco de churn elevado.",
        idpRepublic: "REPÚBLICA DE ANGOLA",
        idpBiPlaceholder: "Número de BI: ███-██████-██-█",
        idpExtractionTitle: "Extracção Inteligente",
        idpLoading: "⠿ a aguardar…",
        fieldOpsAsk: "Pergunte sobre a operação em Português…",
    },
    placeholder: {
        title: "Módulo em construção",
        body: "Este módulo será activado nas próximas iterações. Os fundamentos da plataforma estão prontos.",
        eta: "Disponível em sessões seguintes",
    },
    services: {
        title: "Serviços & Provisionamento",
        subtitle: "Pipeline de activações e SLA — Angola Telecom.",
        new: "Nova Ordem de Serviço",
        kpiActivated: "Activações Hoje",
        kpiPendingApproval: "Em Aprovação",
        kpiSlaAtRisk: "SLA em Risco",
        kpiResolvedMonth: "Activos Totais",
        col_order: "Ordem",
        col_customer: "Cliente",
        col_service: "Serviço",
        col_status: "Estado",
        col_priority: "Prioridade",
        col_sla: "SLA",
        col_assignee: "Atribuído",
        col_created: "Criado",
        empty: "Nenhuma ordem encontrada.",
        backToList: "Voltar à lista",
        approve: "Aprovar",
        reject: "Rejeitar",
        activate: "Activar",
        startProvisioning: "Iniciar Provisionamento",
        cancel: "Cancelar",
        assign: "Atribuir",
        workflow: "Fluxo de Trabalho",
        documentsPanel: "Documentos",
        linkedTickets: "Tickets ligados",
        notes: "Notas",
        slaBreached: "SLA ultrapassado",
        slaInTime: "Dentro do SLA",
        status_draft: "Rascunho",
        status_pending_docs: "Documentos pendentes",
        status_pending_approval: "Em aprovação",
        status_approved: "Aprovada",
        status_in_provisioning: "Em provisionamento",
        status_active: "Activa",
        status_rejected: "Rejeitada",
        status_cancelled: "Cancelada",
        priority_low: "Baixa",
        priority_medium: "Média",
        priority_high: "Alta",
        priority_critical: "Crítica",
        step_pending: "Pendente",
        step_in_progress: "Em curso",
        step_completed: "Concluída",
        step_skipped: "Ignorada",
        step_failed: "Falhada",
    },
    wizard: {
        step1: "Cliente",
        step2: "Documentos",
        step3: "Serviço",
        step4: "Revisão",
        step5: "Confirmação",
        validationNamePhone: "Nome e telefone são obrigatórios.",
        step1Title: "Dados do Cliente",
        step1Sub: "Pesquise por nome, telefone ou BI. Ou crie um novo cliente.",
        step2Title: "Verificação de Documentos",
        step2Sub: "Carregue os documentos do cliente para extração automática por IA.",
        step3Title: "Selecção do Serviço",
        step3Sub: "Escolha o serviço a provisionar.",
        step4Title: "Revisão Final",
        step4Sub: "Confirme os dados antes de criar a ordem.",
        step5Title: "Ordem Criada com Sucesso",
        step5Sub: "A ordem foi adicionada ao pipeline de provisionamento.",
        existingCustomer: "Cliente existente",
        newCustomer: "Novo cliente",
        next: "Continuar",
        back: "Voltar",
        finish: "Criar Ordem",
        searchCustomer: "Procurar cliente…",
        uploadDoc: "Carregar Documento",
        chooseDocType: "Tipo de documento",
        extractAi: "Extrair com IA",
        timerLabel: "Tempo desta sessão",
        viewOrder: "Ver Ordem",
        backToServices: "Voltar a Serviços",
        documentReady: "Pronto para revisão",
        documentProcessing: "A processar…",
        documentVerified: "Verificado",
        priorityLabel: "Prioridade",
        notesLabel: "Notas (opcional)",
    },
    customers: {
        title: "Diretório de Clientes",
        subtitle: "Base de dados unificada — Angola Telecom.",
        new: "Novo Cliente",
        searchPlaceholder: "Pesquisar por nome, telefone, BI…",
        churn_stable: "Cliente estável",
        churn_attention: "Atenção",
        churn_highRisk: "Risco elevado",
        types: { individual: "Particular", enterprise: "Empresarial", government: "Governo" },
        statuses: { prospect: "Prospecto", active: "Activo", suspended: "Suspenso", churned: "Churn" },
        filterType: "Todos os tipos",
        filterStatus: "Todos os estados",
        col_name: "Nome",
        col_type: "Tipo",
        col_phone: "Telefone",
        col_lifetime: "LTV (AOA)",
        col_status: "Estado",
        col_risk: "Risco Churn",
        empty: "Nenhum cliente encontrado.",
        tabs: { info: "Informações", services: "Serviços", documents: "Documentos", tickets: "Tickets", history: "Histórico" },
        churnLow: "Baixo",
        churnMed: "Médio",
        churnHigh: "Alto",
    },
    tickets: {
        title: "Fila de Tickets",
        subtitle: "Gestão centralizada do serviço ao cliente.",
        new: "Novo Ticket",
        statuses: {
            aberto: "Aberto",
            em_progresso: "Em Progresso",
            pendente_cliente: "Pendente Cliente",
            resolvido: "Resolvido",
            fechado: "Fechado",
        },
        categories: {
            provisioning: "Provisionamento",
            billing: "Facturação",
            technical: "Técnico",
            complaint: "Reclamação",
            general: "Geral",
        },
        priority_low: "Baixa",
        priority_medium: "Média",
        priority_high: "Alta",
        priority_critical: "Crítica",
        view_kanban: "Kanban",
        view_table: "Tabela",
        filterAll: "Todas",
        allCategories: "Todas as categorias",
        allPriorities: "Todas as prioridades",
        addComment: "Adicionar comentário…",
        publishComment: "Publicar",
        internalNote: "Nota interna",
        publicReply: "Resposta ao cliente",
        timeline: "Histórico",
        slaRemaining: "SLA restante",
        slaBreached: "SLA vencido",
        backToList: "Voltar à fila",
        info: "Informações",
        empty: "Não há tickets nesta coluna.",
        validationRequired: "Assunto e descrição são obrigatórios.",
        noCustomer: "Sem cliente associado",
        col_number: "Número",
        col_subject: "Assunto",
        col_customer: "Cliente",
        col_category: "Categoria",
        col_priority: "Prioridade",
        col_status: "Estado",
        col_sla: "SLA",
    },
    errors: {
        generic: "Ocorreu um erro inesperado. Tente novamente.",
        network: "Sem ligação ao servidor. Verifique a sua rede.",
        unauthorised: "Sem permissão para esta acção.",
    },
    conversations: {
        title: "Conversas",
        subtitle: "Caixa de entrada omnichannel — Angola Telecom.",
        inbox: "Caixa de Entrada",
        searchPlaceholder: "Pesquisar mensagens, cliente…",
        filterAll: "Todos os canais",
        filterStatus: "Todos os estados",
        channels: {
            web_chat: "Chat Web",
            whatsapp: "WhatsApp",
            email: "Email",
            voice: "Voz",
        },
        statuses: {
            active: "Activa",
            waiting: "A aguardar",
            escalated: "Escalada",
            closed: "Fechada",
        },
        empty: "Nenhuma conversa encontrada.",
        emptyThread: "Seleccione uma conversa para ver o histórico.",
        composerPlaceholder: "Escreva a sua resposta…",
        send: "Enviar",
        sendInternal: "Nota interna",
        triage: "Análise IA",
        triageRun: "Classificar com IA",
        triageUrgency: "Urgência",
        triageIntent: "Intenção",
        triageSentiment: "Sentimento",
        triageSummary: "Resumo",
        triageActions: "Acções sugeridas",
        triageTags: "Tags KB sugeridas",
        draft: "Rascunho de Resposta",
        draftGenerate: "Gerar com IA",
        draftRegenerate: "Regenerar",
        draftInsert: "Inserir no editor",
        draftTone: "Tom",
        toneProfissional: "Profissional",
        toneEmpatico: "Empático",
        toneFormal: "Formal",
        escalate: "Escalar para humano",
        escalated: "Conversa escalada",
        close: "Fechar conversa",
        closed: "Conversa fechada",
        csat: "Avaliação do cliente",
        urgency: {
            baixa: "Baixa",
            media: "Média",
            alta: "Alta",
            critica: "Crítica",
        },
        intent: {
            faturacao: "Facturação",
            suporte_tecnico: "Suporte Técnico",
            provisionamento: "Provisionamento",
            reclamacao: "Reclamação",
            informacao: "Informação",
            outro: "Outro",
        },
        sentiment: {
            muito_negativo: "Muito Negativo",
            negativo: "Negativo",
            neutro: "Neutro",
            positivo: "Positivo",
            muito_positivo: "Muito Positivo",
        },
        kpiOpen: "Conversas Abertas",
        kpiEscalated: "Escaladas",
        kpiSentiment: "Sentimento Médio",
        kpiCsat: "CSAT Médio",
    },
    kb: {
        title: "Base de Conhecimento",
        subtitle: "Artigos e procedimentos — Angola Telecom.",
        new: "Novo Artigo",
        searchPlaceholder: "Pesquisar artigos…",
        filterAll: "Todas as categorias",
        categories: {
            faturacao: "Facturação",
            instalacao: "Instalação",
            suporte_tecnico: "Suporte Técnico",
            comercial: "Comercial",
            geral: "Geral",
        },
        views: "visualizações",
        helpful: "Útil",
        empty: "Sem artigos para esta pesquisa.",
        readMore: "Ler mais",
        backToList: "Voltar à lista",
    },
    sales: {
        title: "Vendas B2B",
        subtitle: "Pipeline empresarial — Angola Telecom.",
        kpiPipeline: "Pipeline Total (AOA)",
        kpiLeads: "Leads Este Mês",
        kpiConversion: "Taxa Conversão",
        kpiProposals: "Propostas Enviadas",
        funnelTitle: "Funil de Vendas",
        dealsTitle: "Negócios em Curso",
        newDeal: "Novo Negócio",
        copilot: "Abrir Co-Piloto",
        viewProposals: "Ver Propostas",
        viewPipeline: "Ver Pipeline",
        col_deal: "Negócio",
        col_company: "Empresa",
        col_stage: "Estado",
        col_value: "Valor",
        col_owner: "Responsável",
        col_close: "Previsão Fecho",
        col_days: "Dias no Estado",
        stages: {
            lead: "Lead",
            qualificado: "Qualificado",
            proposta: "Proposta",
            negociacao: "Negociação",
            fechado_ganho: "Fechado (Ganho)",
            fechado_perdido: "Fechado (Perdido)",
        },
    },
    copilot: {
        title: "Co-Piloto de Vendas IA",
        subtitle: "Descoberta, recomendação e proposta — em tempo real.",
        contextTitle: "Contexto do Negócio",
        companyLabel: "Empresa do Cliente",
        problemLabel: "Problema do Cliente",
        problemPlaceholder: "Descreva o desafio do cliente em detalhe…",
        startBtn: "Iniciar Co-Piloto",
        chatPlaceholder: "Escreva a resposta do cliente ou adicione contexto…",
        send: "Enviar",
        recommendationsTitle: "Recomendações em Tempo Real",
        recommendationsEmpty: "As recomendações aparecerão aqui à medida que a conversa avança.",
        generateProposal: "Gerar Proposta Agora",
        generatingProposal: "A IA está a redigir a sua proposta…",
        proposalReady: "Proposta gerada com sucesso!",
        downloadPdf: "Descarregar PDF",
        sendEmail: "Enviar por Email",
        viewProposal: "Ver Proposta",
        thinking: "A pensar…",
        analyzingNeeds: "A analisar necessidades…",
        productsIdentified: "Produtos identificados",
        readyForProposal: "Pronto para gerar proposta!",
        aiCopilot: "Co-Piloto IA",
        agent: "Comercial",
    },
    proposals: {
        title: "Propostas",
        subtitle: "Gestão de propostas comerciais.",
        col_number: "Número",
        col_customer: "Cliente",
        col_total: "Total",
        col_status: "Estado",
        col_created: "Criada",
        statuses: {
            draft: "Rascunho",
            sent: "Enviada",
            viewed: "Visualizada",
            accepted: "Aceite",
            rejected: "Rejeitada",
        },
        downloadPdf: "PDF",
        empty: "Sem propostas para mostrar.",
    },
    analytics: {
        title: "Analítica Executiva",
        subtitle: "Inteligência operacional e narrativa IA — Angola Telecom.",
        execSummary: "Resumo Executivo IA",
        execSummaryEmpty: "Ainda não foi gerado nenhum resumo. Clique abaixo para criar o primeiro.",
        refresh: "Actualizar Resumo",
        generating: "A IA está a analisar os dados desta semana…",
        period: "Período",
        periodDay: "Último dia",
        periodWeek: "Última semana",
        periodMonth: "Último mês",
        headline: "Manchete",
        highlights: "Pontos Fortes",
        risks: "Riscos a Vigiar",
        keyMetrics: "Métricas-Chave",
        trends: "Tendências (180 dias)",
        chartActivations: "Activações Diárias",
        chartCSAT: "CSAT Médio",
        chartSLA: "Conformidade SLA Campo (%)",
        chartPipeline: "Pipeline B2B (USD)",
    },
    revenue: {
        title: "Garantia de Receita",
        subtitle: "Detecção de anomalias, fraude e perdas de receita.",
        atRisk: "Valor em Risco (Aberto)",
        openAnomalies: "Anomalias em Aberto",
        totalAnomalies: "Total de Anomalias",
        activeAnomalies: "anomalias activas",
        col_type: "Tipo",
        col_severity: "Severidade",
        col_amount: "Em Risco",
        col_status: "Estado",
        col_description: "Descrição",
        col_customer: "Cliente",
        col_actions: "Acções",
        types: {
            unbilled_service: "Serviço não facturado",
            billing_discrepancy: "Discrepância de cobrança",
            fraud_pattern: "Padrão de fraude",
            unusual_usage: "Uso anómalo",
            subscription_abuse: "Abuso de subscrição",
        },
        severities: {
            low: "Baixa",
            medium: "Média",
            high: "Alta",
            critical: "Crítica",
        },
        statuses: {
            open: "Aberto",
            under_investigation: "Em investigação",
            resolved: "Resolvido",
            dismissed: "Descartado",
        },
        filterSeverity: "Todas as severidades",
        filterStatus: "Todos os estados",
        filterType: "Todos os tipos",
        empty: "Sem anomalias detectadas.",
        generateNotes: "Notas IA",
        markInvestigation: "Em investigação",
        markResolved: "Resolver",
        aiCaseNotes: "Notas de Investigação IA",
    },
    nexustel: {
        title: "Pergunta ao NexusTel",
        subtitle: "Assistente operacional em linguagem natural.",
        placeholder: "Ex: Quantos tickets críticos temos hoje?",
        ask: "Perguntar",
        thinking: "A pensar…",
        suggestionsLabel: "Sugestões",
        suggestions: [
            "Quantos tickets críticos temos hoje?",
            "Qual a receita em risco actualmente?",
            "Quantos novos clientes esta semana?",
            "Qual o valor total do pipeline B2B?",
            "Quantas conversas escaladas estão abertas?",
        ],
        morningBriefing: "Briefing Matinal",
        refreshBriefing: "Actualizar",
        briefingLoading: "A preparar o seu briefing matinal…",
    },
    integrations: {
        title: "Hub de Integrações",
        subtitle: "Conecte a Angola Telecom a qualquer sistema empresarial.",
        newConnector: "Novo Conector",
        kpiTotal: "Conectores",
        kpiActive: "Activos",
        kpiEvents24h: "Eventos 24h",
        kpiErrorRate: "Taxa de Erro",
        statuses: {
            active: "Activo",
            inactive: "Inactivo",
            error: "Erro",
            configuring: "A configurar",
        },
        types: {
            rest_api: "REST API",
            webhook: "Webhook",
            sftp: "SFTP",
            oauth2: "OAuth2",
        },
        lastSync: "Última sincronização",
        events: "eventos",
        errorRate: "erro",
        view: "Ver",
        test: "Testar",
        recentEvents: "Eventos Recentes",
        eventInspector: "Inspector de Eventos",
        mappings: "Mapeamentos",
        webhookUrl: "URL do Webhook",
        webhookSecret: "Segredo do Webhook",
        secretWarning: "Guarde este segredo — não será mostrado novamente.",
        retry: "Tentar novamente",
        copySecret: "Copiar",
        copied: "Copiado!",
        wizardTitle: "Novo Conector",
        wizardStep1: "Tipo & Fornecedor",
        wizardStep2: "Autenticação",
        wizardStep3: "Mapeamento de Campos",
        wizardStep4: "Testar",
        wizardStep5: "Activar",
        next: "Seguinte",
        back: "Voltar",
        save: "Guardar",
        cancel: "Cancelar",
        sourceField: "Campo Origem",
        targetField: "Campo Destino",
        transform: "Transformação",
        addMapping: "Adicionar Mapeamento",
        scheduler: "Cron de Briefing Matinal",
        schedulerNext: "Próxima execução",
        schedulerRunNow: "Executar Agora",
        empty: "Sem eventos para este conector.",
    },
    admin: {
        sectionTitle: "Administração da Plataforma",
        sectionSubtitle: "Utilizadores, auditoria, permissões e configurações — Angola Telecom.",        tabs: {
            users: "Utilizadores",
            audit: "Auditoria",
            rbac: "Permissões (RBAC)",
            settings: "Sistema",
            tenants: "Organização",
        },
        audit: {
            title: "Registo de Auditoria",
            subtitle: "Histórico imutável de eventos da plataforma.",
            search: "Pesquisar acção…",
            allActions: "Todas as acções",
            allResources: "Todos os recursos",
            from: "De",
            to: "Até",
            export: "Exportar CSV",
            col_when: "Quando",
            col_user: "Utilizador",
            col_action: "Acção",
            col_resource: "Recurso",
            col_ip: "IP",
            col_details: "Detalhes",
            empty: "Sem registos para os filtros seleccionados.",
            system: "Sistema",
            reset: "Limpar filtros",
        },
        rbac: {
            title: "Matriz de Permissões",
            subtitle: "Mapeamento de módulos por perfil. Apenas leitura.",
            module: "Módulo",
            allowed: "Permitido",
            denied: "Negado",
            legendHint: "Cinzento = negado · Cobre = permitido",
        },
        settings: {
            title: "Configurações do Sistema",
            subtitle: "Funcionalidades e canais de notificação.",
            features: "Funcionalidades activas",
            notifications: "Canais de Notificação",
            channel_email: "Email (SendGrid)",
            channel_inapp: "Notificações na app",
            channel_sms: "SMS",
            sendgridConfigured: "SendGrid configurado",
            sendgridMissing: "SendGrid em modo fallback (logs)",
            save: "Guardar alterações",
            saved: "Configurações guardadas.",
            feature: {
                service_ops: "Serviços & Provisionamento",
                field_ops: "Operações de Campo",
                cx_platform: "Plataforma CX",
                sales_intelligence: "Inteligência de Vendas",
                revenue_ops: "Garantia de Receita",
                analytics: "Analítica Executiva",
                integrations: "Hub de Integrações",
                admin: "Administração",
            },
        },
        tenants: {
            title: "Organização",
            subtitle: "Detalhes do tenant e uso da plataforma.",
            org: "Organização",
            slug: "Identificador",
            timezone: "Fuso horário",
            currency: "Moeda",
            language: "Idioma",
            stats: "Estatísticas de Utilização",
            stat_users: "Utilizadores",
            stat_customers: "Clientes",
            stat_orders: "Ordens de Serviço",
            stat_tickets: "Tickets",
            stat_connectors: "Conectores",
        },
    },
    offline: {
        banner: "Sem ligação — modo offline activo",
        restored: "Ligação restaurada",
    },
    kpiLabels: {
        activations_today: "Activações Hoje",
        open_tickets: "Tickets Abertos",
        csat_avg: "CSAT Médio",
        pipeline_b2b: "Pipeline B2B",
    },
    demoBanner: {
        title: "Ambiente de Demonstração · Angola Telecom",
        snapshot: "Snapshot curado",
        resetHint: "repor via",
    },
    csat: {
        retentionAlerts: "Alertas de Retenção (CSAT)",
        alerts: "alertas",
    },
    activityTypes: {
        "user.login": "Início de sessão",
        "user.logout": "Sessão terminada",
        "service.created": "Serviço criado",
        "service.activated": "Serviço activado",
        "ticket.opened": "Ticket aberto",
        "ticket.resolved": "Ticket resolvido",
        "integration.synced": "Integração sincronizada",
        "payment.received": "Pagamento recebido",
    },
    fieldOps: {
        subtitle: "Despacho inteligente, mapa em tempo real e NLQ em Português.",
        statuses: {
            por_atribuir: "Por Atribuir",
            atribuido: "Atribuído",
            em_deslocamento: "Em Deslocamento",
            em_progresso: "Em Progresso",
            concluido: "Concluído",
            nao_cumprido: "Não Cumprido",
        },
        kpiTechsAvailable: "Técnicos Disponíveis",
        nlqPlaceholder: "Mostra-me os técnicos disponíveis em Talatona…",
        aiAssign: "Atribuir IA",
        nlqSamples: [
            "Mostra-me os técnicos disponíveis em Talatona",
            "Quantos trabalhos críticos estão por atribuir?",
            "Quem fechou mais tickets esta semana?",
            "Quais trabalhos estão em progresso?",
        ],
    },
    idp: {
        fields: {
            bi_number: "Número de BI",
            nif: "Número de NIF",
            full_name: "Nome completo",
            gender: "Género",
            date_of_birth: "Data de nascimento",
            address: "Morada",
            phone: "Telefone",
            email: "Email",
        },
        confidenceHigh: "Alta confiança",
        confidenceMed: "Confiança média",
        confidenceLow: "Requere revisão",
        extractionFailed: "Falha na extracção.",
        connectionLost: "Ligação interrompida — tente novamente.",
    },
    askNexustelError: "Não foi possível processar a pergunta neste momento.",
    integrationsExtra: {
        namePlaceholder: "Nome do conector",
        descPlaceholder: "Descrição (opcional)",
        authNone: "Sem autenticação",
        activate: "Activar",
    },
    demoReset: {
        cardTitle: "Reset Demo Data",
        cardDesc: "Apaga e recria a totalidade dos dados de demonstração — utilizadores demo, clientes, ordens, tickets, deals, KPIs, conectores. Operação destrutiva. Pode demorar até 90 segundos.",
        button: "Reset Demo",
        running: "A repor dados de demonstração…",
        success: "✓ Dados de demonstração repostos com sucesso. Recarregue a página.",
        error: "✕ Falha na reposição. Verifique os logs do backend.",
        modalTitle: "Confirmar reposição",
        modalBody: "Esta operação apaga todos os dados de demonstração e recria-os a partir dos seeds.",
        modalBodyStrong: "Não pode ser desfeita.",
        modalBodyTail: "Continuar?",
        confirm: "Sim, repor agora",
    },
    salesExtra: {
        companySample: "BFA Banco de Fomento",
    },
    convExtra: {
        anonymousCustomer: "Cliente Anónimo",
    },
    invoiceModal: {
        title: "Carregar Factura",
        subtitle: "PDF · máx 10 MB · extracção estruturada por Claude",
        dropHint: "Largue aqui o PDF da factura",
        orClick: "ou clique para escolher um ficheiro",
        extracting: "A extrair campos com Claude…",
        uploadDone: "Concluído.",
        emptyHint: "Largue um PDF de factura à esquerda para ver fornecedor, totais, linhas e taxa de IVA aqui.",
        statusOk: "Tudo certo",
        statusWarning: "Aviso",
        statusError: "Erro",
        vendorTaxId: "NIF do Fornecedor",
        currency: "Moeda",
        issueDate: "Data de Emissão",
        dueDate: "Vencimento",
        subtotal: "Subtotal",
        tax: "IVA",
        total: "Total",
        paymentTerms: "Termos de Pagamento",
        vendorAddress: "Morada do Fornecedor",
        customer: "Cliente",
        lineItems: "Linhas",
        colDescription: "Descrição",
        colQty: "Qtd",
        colUnit: "Preço Unit.",
        colTotal: "Total Linha",
        flagsHeader: "Validação",
        createTicket: "Criar ticket a partir da factura",
        openTicket: "Ver ticket",
        copyJson: "Copiar JSON",
        downloadJson: "Descarregar JSON",
        errPickFile: "Escolha um PDF primeiro.",
        errOnlyPdf: "Apenas ficheiros PDF são aceites.",
        errTooLarge: "Ficheiro excede o limite de 10 MB.",
        errEmpty: "Ficheiro vazio.",
        errExtract: "Falha na extracção.",
        errCreateTicket: "Não foi possível criar o ticket.",
        trigger: "Carregar Factura",
    },
};

// ============ Reactive language proxy ============
// Lazy property access on each read — switches language without bundle reload.
// Falls back to Portuguese for any missing English key.
const DICTS = { pt, en };

function getActiveLang() {
    try {
        return localStorage.getItem("nx_lang") === "en" ? "en" : "pt";
    } catch {
        return "pt";
    }
}

function resolveAt(dict, path) {
    let cur = dict;
    for (const k of path) {
        if (cur == null) return undefined;
        cur = cur[k];
    }
    return cur;
}

function makeI18nProxy(path) {
    return new Proxy(
        {},
        {
            get(_t, prop) {
                if (typeof prop !== "string") return undefined;
                const lang = getActiveLang();
                const activeVal = resolveAt(DICTS[lang], [...path, prop]);
                const fallbackVal = resolveAt(DICTS.pt, [...path, prop]);
                const val = activeVal !== undefined ? activeVal : fallbackVal;
                if (val !== null && typeof val === "object" && !Array.isArray(val)) {
                    return makeI18nProxy([...path, prop]);
                }
                return val;
            },
            ownKeys() {
                const lang = getActiveLang();
                const target = resolveAt(DICTS[lang], path) || resolveAt(DICTS.pt, path) || {};
                return Reflect.ownKeys(target);
            },
            getOwnPropertyDescriptor() {
                return { enumerable: true, configurable: true };
            },
        },
    );
}

const i18nProxy = makeI18nProxy([]);
export { i18nProxy as i18n };

export default i18nProxy;




